/* ============================================================
   MT Theme Details — Preview Section JS
   ============================================================ */

   (function () {
    'use strict';
  
    /**
     * 管理单个 mt-theme-details 区块的预览逻辑：
     * - 三设备宽度切换（PC / 平板 / 手机）
     * - CSS transform scale 缩放以填满容器
     * - iframe 加载状态管理
     * - ResizeObserver 响应容器宽度变化
     */
    class MtThemeDetailsSection {
      constructor(el) {
        this.el = el;
        this.sectionId = el.dataset.sectionId;
        this.previewHeight = parseInt(el.dataset.previewHeight, 10) || 700;
        this.currentWidth = 1440;
        this._switchTimer = null;
  
        this._bindElements();
        this._applySidebarCategory();
        this._bindDeviceButtons();
        this._bindCopyLinkBtn();
        this._bindFullscreenBtn();
        this._bindVariantButtons();
        this._initBlurUpImages();
        this._initResizeObserver();
        this._initPreviewAndDevice();
      }

      async _initPreviewAndDevice() {
        await this._resolvePreviewUrl();
        this._setDevice(1440, this.el.querySelector('[data-device="desktop"]'), false);
      }
  
      /* ── 元素绑定 ─────────────────────────────────────────── */
  
      _bindElements() {
        this.stage       = this.el.querySelector('[data-preview-stage]');
        this.viewport    = this.el.querySelector('[data-preview-viewport]');
        this.iframe      = this.el.querySelector('[data-preview-iframe]');
        this.loading     = this.el.querySelector('[data-preview-loading]');
        this.badge       = this.el.querySelector('[data-width-badge]');
        this.buttons     = this.el.querySelectorAll('[data-device-btn]');
        this.urlDisplay  = this.el.querySelector('[data-chrome-url]');
        this.newTabLink  = this.el.querySelector('[data-chrome-new-tab]');
        this.placeholder = this.el.querySelector('[data-preview-placeholder]');
        this.previewWrap = this.el.querySelector('.mt-theme-details__preview-wrap');
        this.previewTopbar = this.el.querySelector('[data-preview-topbar]');
        this.previewTopbarLabel = this.el.querySelector('[data-preview-topbar-label]');
        this.copyLinkBtn = this.el.querySelector('[data-copy-link-btn]');
      }
  
      /* ── 左侧分类标签（Shop 筛选组 / 选项） ─────────────── */

      _applySidebarCategory() {
        const el = this.el.querySelector('[data-sidebar-category]');
        if (!el) return;

        const params = new URLSearchParams(window.location.search);
        const group  = (params.get('mtd_group') || '').trim();
        const cat    = (params.get('mtd_cat') || '').trim();

        if (group && cat) {
          el.textContent = `${group.toUpperCase()} / ${cat.toUpperCase()}`;
          el.hidden = false;
        } else if (cat) {
          el.textContent = cat.toUpperCase();
          el.hidden = false;
        }
      }

      /* ── 解析预览 URL（URL 参数 > preview_handle 目录 > 区块静态设置） ── */

      static get _NAV_HIDE_SELECTORS() {
        return [
          '.mt-top-nav__container',
          '.mt-bottom-nav__container',
          '.mt-bottom-nav',
          '.mt-top-nav--light',
        ];
      }

      static get _NAV_HIDE_CSS() {
        return [
          '.mt-top-nav__container,',
          '.mt-bottom-nav,',
          '.mt-top-nav--light,',
          '.mt-bottom-nav__container {',
          'display: none !important;',
          'height: 0 !important;',
          'min-height: 0 !important;',
          'overflow: hidden !important;',
          'visibility: hidden !important;',
          '}',
        ].join('\n');
      }

      /** iframe 高度在自适应计算后额外减去的偏移量（px） */
      static get _IFRAME_HEIGHT_OFFSET() {
        return 70;
      }

      _isSameOrigin(url) {
        try {
          return new URL(url, window.location.origin).origin === window.location.origin;
        } catch (_) {
          return false;
        }
      }

      _buildProxyUrl(rawUrl) {
        const proxyBase = (this.el.dataset.proxyUrl || '').trim();
        if (!proxyBase || !rawUrl) return '';
        return `${proxyBase.replace(/\/$/, '')}?url=${encodeURIComponent(rawUrl)}`;
      }

      _cleanPreviewHtml(html, pageUrl) {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        MtThemeDetailsSection._NAV_HIDE_SELECTORS.forEach((selector) => {
          doc.querySelectorAll(selector).forEach((node) => node.remove());
        });

        const style = doc.createElement('style');
        style.setAttribute('data-mtd-nav-hide', '');
        style.textContent = MtThemeDetailsSection._NAV_HIDE_CSS;
        doc.head.appendChild(style);

        let base = doc.querySelector('base');
        if (!base) {
          base = doc.createElement('base');
          doc.head.prepend(base);
        }
        try {
          base.href = new URL(pageUrl, window.location.origin).href;
        } catch (_) {}

        return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
      }

      async _fetchPreviewHtml(rawUrl) {
        const targets = [];
        const proxyUrl = this._buildProxyUrl(rawUrl);

        if (proxyUrl) targets.push(proxyUrl);
        if (this._isSameOrigin(rawUrl)) targets.push(rawUrl);

        for (const target of targets) {
          try {
            const res = await fetch(target, {
              credentials: target === rawUrl ? 'same-origin' : 'omit',
              cache: 'no-store',
            });
            if (!res.ok) continue;

            const html = await res.text();
            if (html.trim()) return html;
          } catch (_) {}
        }

        return null;
      }

      _bindIframeLoadOnce(onLoad) {
        const hideLoading = () => {
          if (this.loading) this.loading.classList.add('is-hidden');
          clearTimeout(this._loadTimeout);
        };

        this.iframe.addEventListener('load', () => {
          hideLoading();
          onLoad?.();
        }, { once: true });

        this.iframe.addEventListener('error', () => {
          hideLoading();
          this._showBlocked('页面加载失败，请检查链接是否正确。');
        }, { once: true });

        this._loadTimeout = setTimeout(hideLoading, 30000);
      }

      _mountIframeSrcdoc(html) {
        this._bindIframeLoadOnce(() => this._stripIframeNav());
        this.iframe.removeAttribute('src');
        this.iframe.srcdoc = html;
      }

      async _loadPreview(rawUrl) {
        const url = String(rawUrl || '').trim();
        if (!url) return;

        if (this.loading) this.loading.classList.remove('is-hidden');
        if (this.blocked) this.blocked.style.display = 'none';

        const html = await this._fetchPreviewHtml(url);
        if (html) {
          this._mountIframeSrcdoc(this._cleanPreviewHtml(html, url));
          return;
        }

        const iframeUrl = this._buildProxyUrl(url) || url;
        await this._prefetchCheck(iframeUrl);
      }

      async _resolveDemoFromHandle(handle) {
        const key = String(handle || '').trim();
        if (!key) return '';

        const catalog = await this._loadShopDemoCatalog();
        const entry = catalog.get(key);
        if (entry?.demoUrl) return entry.demoUrl;

        const link = this.el.querySelector(
          `.mt-theme-details__sidebar-variant[data-product-handle="${key}"]`
        );
        return (link?.dataset.fallbackDemoUrl || '').trim();
      }

      async _resolvePreviewUrl() {
        const params = new URLSearchParams(window.location.search);
        const previewParam = (params.get('preview') || '').trim();
        const previewHandle = (params.get('preview_handle') || '').trim();

        let rawUrl = previewParam;

        if (!rawUrl && previewHandle) {
          rawUrl = await this._resolveDemoFromHandle(previewHandle);
        }

        if (!rawUrl && !previewHandle) {
          rawUrl = (this.el.dataset.staticUrl || '').trim();
        }

        if (rawUrl) {
          if (this.urlDisplay) this.urlDisplay.textContent = rawUrl;
          if (this.newTabLink) {
            this.newTabLink.href = rawUrl;
            this.newTabLink.removeAttribute('hidden');
          }
          const blockedLink = this.el.querySelector('[data-blocked-link]');
          if (blockedLink) blockedLink.href = rawUrl;
          if (this.placeholder) this.placeholder.style.display = 'none';
          if (this.iframe) this.iframe.style.display = '';

          await this._loadPreview(rawUrl);
        } else {
          if (this.iframe) this.iframe.style.display = 'none';
          if (this.placeholder) {
            this.placeholder.style.display = 'flex';
            if (previewHandle) {
              const sub = this.placeholder.querySelector('.mt-theme-details__placeholder-sub');
              if (sub) {
                sub.textContent = '未找到该商品对应的 Demo URL，请确认 Shop 卡片已填写预览链接，或先访问 Shop 列表页后再试。';
              }
              if (this.urlDisplay) {
                this.urlDisplay.textContent = 'Demo URL not found';
              }
            }
          }
          if (this.loading) this.loading.classList.add('is-hidden');
        }

        this._finalUrl = rawUrl;
      }
  
      /**
       * ── fetch 预检 → 再加载 iframe ──────────────────────────
       *
       * 为什么不直接设 iframe.src？
       * 空 <iframe>（无 src）会立刻对 about:blank 触发 load 事件。
       * 若在那之前就用 { once:true } 绑定监听，监听器会被 about:blank 消耗掉，
       * 后续真正设置 src 时 load 事件将无人处理，loading 永远不消失。
       *
       * 解决方案：
       *   1. fetch 预检确认服务器可达
       *   2. 确认后在 _startIframeLoad 里先绑定事件、再立即设置 src
       *      → 中间没有 about:blank 插队的机会
       */
      async _prefetchCheck(url) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 10000);
  
        try {
          await fetch(url, { mode: 'no-cors', cache: 'no-store', signal: controller.signal });
          clearTimeout(timer);
          this._startIframeLoad(url);
        } catch (err) {
          clearTimeout(timer);
          if (err.name === 'AbortError') {
            // 响应慢但未彻底失联，继续尝试加载
            this._startIframeLoad(url);
          } else {
            // ERR_CONNECTION_REFUSED / DNS 失败 → 服务器不可达
            if (this.loading) this.loading.classList.add('is-hidden');
            this._showBlocked('无法连接到目标服务器，该地址可能不存在或已下线。');
          }
        }
      }
  
      /**
       * 先绑定事件，紧接着设置 iframe.src。
       * 保证 load / error 监听器在 src 赋值前就已就位，
       * 且此时 iframe 还没有 src（不会再有 about:blank 插队）。
       */
      _startIframeLoad(url) {
        this._bindIframeLoadOnce(() => {
          this._stripIframeNav();
          try {
            const doc = this.iframe.contentDocument;
            if (doc && doc.body && doc.body.innerHTML.trim() === '') {
              this._showBlocked('页面加载为空，目标地址可能不支持在 iframe 中嵌入。');
            }
          } catch (_) {
            // SecurityError = 跨域，正常情况，不报错
          }
        });

        this.iframe.removeAttribute('srcdoc');
        this.iframe.src = url;
      }

      /**
       * iframe 内移除 .mt-top-nav__container / .mt-bottom-nav__container
       */
      _stripIframeNav() {
        try {
          const doc = this.iframe.contentDocument;
          if (!doc) return;

          MtThemeDetailsSection._NAV_HIDE_SELECTORS.forEach((selector) => {
            doc.querySelectorAll(selector).forEach((node) => node.remove());
          });

          if (!doc.querySelector('[data-mtd-nav-hide]')) {
            const style = doc.createElement('style');
            style.setAttribute('data-mtd-nav-hide', '');
            style.textContent = MtThemeDetailsSection._NAV_HIDE_CSS;
            doc.head.appendChild(style);
          }
        } catch (_) {
          /* 跨域 iframe 无法访问 DOM，需配置 proxy_url 以走 fetch 清理 HTML */
        }
      }
  
      _showBlocked(reason) {
        this.blocked = this.blocked || this.el.querySelector('[data-preview-blocked]');
        this.blockedLink = this.blockedLink || this.el.querySelector('[data-blocked-link]');
        if (!this.blocked) return;
  
        this.blocked.style.display = 'flex';
  
        if (reason) {
          const desc = this.blocked.querySelector('[data-blocked-desc]');
          if (desc) desc.textContent = reason;
        }
  
        const linkSrc = this._finalUrl || '';
        if (this.blockedLink && linkSrc) this.blockedLink.href = linkSrc;
      }
  
      /* ── 设备切换按钮 ─────────────────────────────────── */
  
      _bindDeviceButtons() {
        this.buttons.forEach(btn => {
          btn.addEventListener('click', () => {
            // 点击设备按钮时，若当前处于全屏状态则退出
            if (this.el.classList.contains('is-fullscreen')) {
              this.el.classList.remove('is-fullscreen');
              if (this._fullscreenBtn) {
                this._fullscreenBtn.setAttribute('aria-pressed', 'false');
                this._fullscreenBtn.classList.remove('is-active');
              }
            }
            const width = parseInt(btn.dataset.width, 10);
            this._setDevice(width, btn, true);
          });
        });
      }
  
      /* ── 设置当前设备 ─────────────────────────────────── */

      _setDevice(width, activeBtn, animate) {
        this.currentWidth = width;
  
        // 更新按钮激活状态
        this.buttons.forEach(btn => {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
        });
        if (activeBtn) {
          activeBtn.classList.add('is-active');
          activeBtn.setAttribute('aria-pressed', 'true');
        }
  
        // 同步更新 preview-wrap 的最大宽度（CSS 负责过渡动画）
        if (this.previewWrap) {
          if (!animate) {
            // 初始化时跳过动画，下一帧再恢复 transition
            this.previewWrap.style.transition = 'none';
            requestAnimationFrame(() => {
              this.previewWrap.style.transition = '';
            });
          }
          this.previewWrap.style.maxWidth = width + 'px';
        }

        if (this.previewTopbar) {
          if (!animate) {
            this.previewTopbar.style.transition = 'none';
            requestAnimationFrame(() => {
              this.previewTopbar.style.transition = '';
            });
          }
          this.previewTopbar.style.maxWidth = width + 'px';
        }
  
        // 显示宽度角标（切换时短暂出现）
        if (animate && this.stage) {
          this.stage.classList.add('is-switching');
          if (this.badge) this.badge.textContent = width + 'px';
          clearTimeout(this._switchTimer);
          this._switchTimer = setTimeout(() => {
            this.stage.classList.remove('is-switching');
          }, 1200);
        }
  
        this._recalculate(animate);
        this._updatePreviewTopbar(activeBtn);
      }

      _updatePreviewTopbar(activeBtn) {
        if (!this.previewTopbarLabel || !activeBtn) return;

        const width = parseInt(activeBtn.dataset.width, 10) || this.currentWidth;
        const labelEn = (activeBtn.dataset.previewLabelEn || 'Desktop preview').trim();

        this.previewTopbarLabel.textContent = `${labelEn} · ${width} px`;
      }

      _bindCopyLinkBtn() {
        if (!this.copyLinkBtn) return;

        this.copyLinkBtn.addEventListener('click', async () => {
          const url = window.location.href;
          const copyTextEl = this.copyLinkBtn.querySelector('.mt-theme-details__preview-topbar-copy-text');
          const copySubEl = this.copyLinkBtn.querySelector('.mt-theme-details__preview-topbar-copy-sub');
          const copiedLabel = 'Copied';
          const copiedSub = '(Copied)';
          const defaultLabel = 'Copy link';

          try {
            await navigator.clipboard.writeText(url);
          } catch (_) {
            const ta = document.createElement('textarea');
            ta.value = url;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
          }

          this.copyLinkBtn.classList.add('is-copied');
          if (copyTextEl) copyTextEl.textContent = copiedLabel;
          if (copySubEl) copySubEl.textContent = copiedSub;

          clearTimeout(this._copyResetTimer);
          this._copyResetTimer = setTimeout(() => {
            this.copyLinkBtn.classList.remove('is-copied');
            if (copyTextEl) copyTextEl.textContent = defaultLabel;
            if (copySubEl) copySubEl.textContent = defaultSub;
          }, 2000);
        });
      }
  
      /* ── 缩放计算 ─────────────────────────────────────── */
  
      _recalculate(animate) {
        if (!this.stage || !this.viewport) return;

        const stageW  = this.stage.clientWidth;
        const iframeH = Math.max(
          0,
          this.previewHeight - MtThemeDetailsSection._IFRAME_HEIGHT_OFFSET
        );

        // 全屏模式下直接用 stageW 作为目标宽度，scale = 1，viewport 填满全宽
        const isFullscreen = this.el.classList.contains('is-fullscreen');
        const targetW = isFullscreen ? stageW : this.currentWidth;

        // 仅在 iframe 比容器宽时缩小，否则原比例显示
        const scale  = stageW >= targetW ? 1 : stageW / targetW;
        const vW     = Math.round(targetW * scale); // = stageW（含全屏 & 缩放两种情况）

        // 高度固定为设定值，不随 scale 缩小；
        // iframe 实际高度反向补偿（/ scale），经过 transform 后视觉恰好 = iframeH
        const vH            = iframeH;
        const iframeActualH = scale < 1 ? Math.round(iframeH / scale) : iframeH;

        // 初始化 / 全屏切换时跳过 viewport 动画
        if (!animate) {
          this.viewport.style.transition = 'none';
          if (this.iframe) this.iframe.style.transition = 'none';
          requestAnimationFrame(() => {
            this.viewport.style.transition = '';
            if (this.iframe) this.iframe.style.transition = '';
          });
        }

        this.viewport.style.width  = vW + 'px';
        this.viewport.style.height = vH + 'px';
        this.stage.style.height    = vH + 'px';

        if (this.iframe) {
          this.iframe.style.width           = targetW + 'px';
          this.iframe.style.height          = iframeActualH + 'px';
          this.iframe.style.transform       = scale < 1 ? `scale(${scale})` : '';
          this.iframe.style.transformOrigin = 'top left';
        }
      }
  
      /* ── 全屏按钮（CSS class 切换，非原生 Fullscreen API） ── */

      _bindFullscreenBtn() {
        const btn = this.el.querySelector('[data-fullscreen-btn]');
        if (!btn) return;
        this._fullscreenBtn = btn;
        btn.addEventListener('click', () => this._toggleFullscreen());
      }

      _toggleFullscreen() {
        const isFS = this.el.classList.toggle('is-fullscreen');
        if (this._fullscreenBtn) {
          this._fullscreenBtn.setAttribute('aria-pressed', isFS ? 'true' : 'false');
          this._fullscreenBtn.classList.toggle('is-active', isFS);
        }

        if (this.previewWrap) {
          // 暂停 transition，让宽度即时生效，再读取稳定的 stageW
          this.previewWrap.style.transition = 'none';
          if (this.previewTopbar) this.previewTopbar.style.transition = 'none';
          if (isFS) {
            // 清除 JS 写入的 inline maxWidth，CSS !important 规则立即接管
            this.previewWrap.style.maxWidth = '';
            if (this.previewTopbar) this.previewTopbar.style.maxWidth = '';
          } else {
            // 恢复当前设备宽度
            this.previewWrap.style.maxWidth = this.currentWidth + 'px';
            if (this.previewTopbar) this.previewTopbar.style.maxWidth = this.currentWidth + 'px';
          }
          // 两帧：第一帧让浏览器完成布局，第二帧读取稳定值
          requestAnimationFrame(() => {
            this._recalculate(false);
            requestAnimationFrame(() => {
              if (this.previewWrap) this.previewWrap.style.transition = '';
              if (this.previewTopbar) this.previewTopbar.style.transition = '';
            });
          });
        } else {
          requestAnimationFrame(() => this._recalculate(false));
        }
      }

      /* ── Blur-Up 图片淡入 ──────────────────────────────── */

      _initBlurUpImages() {
        this.el.querySelectorAll('.img-hd-source').forEach(img => {
          if (img.complete && img.naturalWidth > 0) {
            img.classList.add('is-loaded');
          } else {
            img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
          }
        });
      }

      /* ── Explore More：从 Shop 页同步 Demo URL ─────────── */

      _mergeCatalogRows(catalog, rows) {
        if (!Array.isArray(rows)) return;
        rows.forEach((row) => {
          const handle = String(row.handle || '').trim();
          const demoUrl = String(row.demoUrl || '').trim();
          if (!handle || !demoUrl || catalog.has(handle)) return;
          catalog.set(handle, {
            demoUrl,
            mtdGroup: String(row.mtdGroup || '').trim(),
            mtdCat: String(row.mtdCat || '').trim(),
          });
        });
      }

      _parseCatalogFromHtml(html) {
        const catalog = new Map();
        if (!html) return catalog;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const jsonEl = doc.querySelector('[data-mt-shop-demo-catalog]');

        if (jsonEl && jsonEl.textContent.trim()) {
          try {
            this._mergeCatalogRows(catalog, JSON.parse(jsonEl.textContent.trim()));
          } catch (_) {
            /* 回退到 DOM 卡片解析 */
          }
        }

        if (!catalog.size) {
          doc.querySelectorAll('[data-mt-shop-card][data-product-handle]').forEach((card) => {
            const handle = (card.dataset.productHandle || '').trim();
            const demoUrl = (card.dataset.demoUrl || '').trim();
            if (!handle || !demoUrl || catalog.has(handle)) return;

            catalog.set(handle, {
              demoUrl,
              mtdGroup: (card.dataset.mtdGroup || '').trim(),
              mtdCat: (card.dataset.mtdCat || '').trim(),
            });
          });
        }

        return catalog;
      }

      _parseCatalogFromJsonText(text) {
        const catalog = new Map();
        if (!text || !text.trim()) return catalog;
        try {
          this._mergeCatalogRows(catalog, JSON.parse(text.trim()));
        } catch (_) {}
        return catalog;
      }

      _mergeCatalogMaps(target, source) {
        source.forEach((value, key) => {
          if (!target.has(key)) target.set(key, value);
        });
      }

      async _fetchCatalogFromPage(pageUrl, sectionId) {
        const catalog = new Map();
        const page = String(pageUrl || '').trim();
        if (!page) return catalog;

        try {
          if (sectionId) {
            const sectionUrl = new URL(page, window.location.origin);
            sectionUrl.searchParams.set('sections', sectionId);
            const sectionRes = await fetch(sectionUrl.toString(), { credentials: 'same-origin' });
            if (sectionRes.ok) {
              const payload = await sectionRes.json();
              const sectionHtml = payload[sectionId];
              this._mergeCatalogMaps(catalog, this._parseCatalogFromHtml(sectionHtml || ''));
              if (catalog.size) return catalog;
            }
          }

          const pageRes = await fetch(page, { credentials: 'same-origin' });
          if (!pageRes.ok) return catalog;
          return this._parseCatalogFromHtml(await pageRes.text());
        } catch (_) {
          return catalog;
        }
      }

      async _loadShopDemoCatalog() {
        if (this._shopCatalogMap && this._shopCatalogMap.size) {
          return this._shopCatalogMap;
        }
        if (this._shopCatalogPromise) return this._shopCatalogPromise;

        this._shopCatalogPromise = (async () => {
          const catalog = new Map();

          const fallbackEl = this.el.querySelector('[data-mt-details-fallback-catalog]');
          if (fallbackEl) {
            this._mergeCatalogMaps(catalog, this._parseCatalogFromJsonText(fallbackEl.textContent));
          }

          try {
            const cached = sessionStorage.getItem('mt-shop-demo-catalog');
            if (cached) {
              this._mergeCatalogMaps(catalog, this._parseCatalogFromJsonText(cached));
            }
          } catch (_) {}

          const catalogPage = (this.el.dataset.shopCatalogPage || '').trim();
          const backPage = (this.el.dataset.sidebarBackUrl || '').trim();
          const sectionId = (this.el.dataset.shopSectionId || '').trim();

          if (catalogPage) {
            this._mergeCatalogMaps(
              catalog,
              await this._fetchCatalogFromPage(catalogPage, sectionId)
            );
          }

          if (backPage && backPage !== catalogPage) {
            this._mergeCatalogMaps(
              catalog,
              await this._fetchCatalogFromPage(backPage, sectionId)
            );
          }

          if (!catalogPage && !backPage) {
            this._mergeCatalogMaps(
              catalog,
              await this._fetchCatalogFromPage(window.location.pathname, sectionId)
            );
          }

          this._shopCatalogMap = catalog;
          return catalog;
        })();

        return this._shopCatalogPromise;
      }

      /* ── 变体按钮切换 ───────────────────────────────────── */

      _bindVariantButtons() {
        const variantBtns = this.el.querySelectorAll('[data-variant-btn]');
        if (!variantBtns.length) return;

        variantBtns.forEach(btn => {
          btn.addEventListener('click', () => {
            const rawUrl = (btn.dataset.previewUrl || '').trim();
            if (!rawUrl) return;

            variantBtns.forEach(b => b.classList.remove('is-active'));
            btn.classList.add('is-active');

            if (this.loading) this.loading.classList.remove('is-hidden');
            if (this.blocked) this.blocked.style.display = 'none';

            if (this.urlDisplay) this.urlDisplay.textContent = rawUrl;
            if (this.newTabLink) {
              this.newTabLink.href = rawUrl;
              this.newTabLink.removeAttribute('hidden');
            }
            const blockedLink = this.el.querySelector('[data-blocked-link]');
            if (blockedLink) blockedLink.href = rawUrl;

            this._finalUrl = rawUrl;

            if (this._loadTimeout) clearTimeout(this._loadTimeout);
            this._loadPreview(rawUrl);
          });
        });
      }

      /* ── ResizeObserver ─────────────────────────────────── */
  
      _initResizeObserver() {
        if (!this.stage) return;
        this._ro = new ResizeObserver(() => this._recalculate(false));
        this._ro.observe(this.stage);
      }
  
      /* ── 销毁 ──────────────────────────────────────────── */
  
      destroy() {
        if (this._ro)          this._ro.disconnect();
        if (this._loadTimeout) clearTimeout(this._loadTimeout);
        if (this._switchTimer) clearTimeout(this._switchTimer);
        if (this._copyResetTimer) clearTimeout(this._copyResetTimer);
        this._shopCatalogPromise = null;
        this._shopCatalogMap = null;
        this.el.classList.remove('is-fullscreen');
      }
    }
  
    /* ── 初始化函数 ─────────────────────────────────────────── */
  
    function init(root) {
      const scope = root || document;
      scope.querySelectorAll('[data-mt-theme-details]').forEach(el => {
        if (el._mtThemeDetails) return;
        el._mtThemeDetails = new MtThemeDetailsSection(el);
      });
    }
  
    /* ── DOMContentLoaded ───────────────────────────────────── */
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => init());
    } else {
      init();
    }
  
    /* ── Shopify Theme Editor 生命周期 ──────────────────────── */
  
    document.addEventListener('shopify:section:load', e => {
      const sec = document.getElementById(
        `mt-theme-details-${e.detail.sectionId}`
      );
      if (!sec) return;
      if (sec._mtThemeDetails) {
        sec._mtThemeDetails.destroy();
        sec._mtThemeDetails = null;
      }
      sec._mtThemeDetails = new MtThemeDetailsSection(sec);
    });
  
    document.addEventListener('shopify:section:unload', e => {
      const sec = document.getElementById(
        `mt-theme-details-${e.detail.sectionId}`
      );
      if (sec && sec._mtThemeDetails) {
        sec._mtThemeDetails.destroy();
        sec._mtThemeDetails = null;
      }
    });
  
    document.addEventListener('shopify:section:select', e => {
      const sec = document.getElementById(
        `mt-theme-details-${e.detail.sectionId}`
      );
      if (sec && !sec._mtThemeDetails) {
        sec._mtThemeDetails = new MtThemeDetailsSection(sec);
      }
    });
  })();
  